<template>
    <div v-if="reassuranceItems.length" class="hero-reassurance" data-testid="hero-reassurance">
        <span
            v-for="(item, index) in reassuranceItems"
            :key="index"
            :class="['hero-reassurance__item', {'hero-reassurance__item--separated': item.hasSeparator}]"
        >
            <span v-if="item.showsRating" class="hero-reassurance__stars">
                <VJoyIcon v-for="star in RATING_STARS" :key="star" name="star-fill" color="quaternary" size="xsmall" />
            </span>
            <MktMaltPrismicImage v-else :image-field="item.image" class="hero-reassurance__image" height="28" />
            <PrismicRichText v-if="item.hasText" :field="item.text" class="hero-reassurance__text" />
        </span>
    </div>
</template>

<script setup lang="ts">
    import {computed} from 'vue';
    import {asText, isFilled} from '@prismicio/helpers';
    import {PrismicRichText} from '@prismicio/vue';
    import type {HeroSliceHeroWithVideoPrimaryReassuranceItem} from '@prismicio-types';
    import {MktMaltPrismicImage} from '#components';
    import {VJoyIcon} from '@maltjoy/core-vue';

    const RATING_STARS = [1, 2, 3, 4, 5];

    const {items} = defineProps<{
        items: HeroSliceHeroWithVideoPrimaryReassuranceItem[];
    }>();

    const reassuranceItems = computed(() => {
        const filledItems = items.filter((item) => isFilled.image(item.image) || isFilled.richText(item.text));

        return filledItems.map((item, index) => {
            const hasText = isFilled.richText(item.text);
            const nextItem = filledItems[index + 1];

            return {
                hasText,
                hasSeparator: !!nextItem,
                image: item.image,
                showsRating: !isFilled.image(item.image) && hasText && asText(item.text).includes('/5'),
                text: item.text,
            };
        });
    });

    const numberOfReassuranceItems = computed(() => reassuranceItems.value.length);
</script>

<style lang="scss">
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/text' as text;
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/media-queries/media-queries' as mq;

    .hero-reassurance {
        --hero-reassurance-gap: var(--joy-core-spacing-9);
        --hero-reassurance-mobile-gap: var(--joy-core-spacing-4);

        display: grid;
        width: 100%;
        padding: 40px var(--joy-core-spacing-4) var(--joy-core-spacing-8);
        grid-template-columns: repeat(2, minmax(0, 1fr));
        gap: var(--joy-core-spacing-4) var(--hero-reassurance-mobile-gap);

        @include mq.screen_md(min) {
            @include text.getFontBySize(primary-400);

            grid-template-columns: repeat(v-bind(numberOfReassuranceItems), minmax(0, max-content));
            justify-content: center;
            gap: var(--hero-reassurance-gap);
        }

        &__item {
            display: flex;
            flex-wrap: wrap;
            align-items: center;
            justify-content: start;
            gap: var(--joy-core-spacing-3);
            min-width: 0;
            padding: var(--joy-core-spacing-2);

            @include mq.screen_xs(min) {
                justify-content: center;
            }

            &--separated {
                position: relative;

                &::after {
                    content: '';
                    position: absolute;
                    top: 0;
                    bottom: 0;
                    right: calc(var(--hero-reassurance-mobile-gap) / -2);
                    width: 1px;
                    background-color: var(--joy-color-neutral-30);
                    transform: translateX(50%);

                    @include mq.screen_sm(min) {
                        right: calc(var(--hero-reassurance-gap) / -2);
                    }
                }

                &:nth-child(even)::after {
                    content: none;

                    @include mq.screen_sm(min) {
                        content: '';
                    }
                }

                @include mq.screen_sm(max) {
                    &:nth-child(2n)::after {
                        display: none;
                    }
                }
            }
        }

        &__stars {
            display: inline-flex;
            flex-shrink: 0;
            align-items: center;
            gap: var(--joy-core-spacing-0);
        }

        &__image {
            display: block;
            flex-shrink: 0;
            width: auto;
            max-width: 100%;
            height: 28px;
            object-fit: contain;
        }

        &__text {
            min-width: 0;
            line-height: var(--joy-line-height-small);
            overflow-wrap: break-word;

            strong {
                color: var(--joy-color-neutral-60);
            }
        }
    }
</style>
